import { useMemo } from "react";
import { Box, Heading, Tooltip, Icon, HStack } from "@chakra-ui/react";
import { Info } from "lucide-react";
import _ from "lodash";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import FilterForm from "../../components/FilterForm";
import TicketsByBranchReport from "../../components/organization/reports/TicketsByBranch";
import Loader from "../../components/loader";
import { getReportDescription, getReportRangeString } from "../../store/helpers";
import { REPORT_RANGES, REPORT_TYPE_IDS } from "../../store/constants";
import DownloadReport from "../../components/DownloadReport";
import SelectBranchInfo from "../../components/misc/SelectBranchInfo";
import ReportPresentationControls from "../../components/reports/ReportPresentationControls";
import ReportPresentationSummary from "../../components/reports/ReportPresentationSummary";
import { TICKET_SUMMARY_COLUMN_DEFINITIONS } from "../../store/renderConfig";
import { useReportPresentation } from "../../hooks/useReportPresentation";
import { downloadTicketSummaryCsvFiles } from "../../store/ticketSummaryExport";

const TicketsPerBranch = ({
  showFilterForm = true,

  showTitle = 1,
}) => {
  const { loading, reportRange, reportsData, organizationId } = useSelector(
    (state) => state.defaultReducer
  );
  const reportTypeId =
    reportRange === REPORT_RANGES.WEEK
      ? REPORT_TYPE_IDS.weekly_organization_tickets_per_branch
      : REPORT_TYPE_IDS.monthly_organization_tickets_per_branch;

  const title = ` Tickets Per Branch`;

  
  const report = reportsData.find(
    (a) =>
      a.reportTypeId === reportTypeId && a.organizationId === organizationId
  );
  const data = report?.data || [];
  const {
    effectiveRenderConfig,
    isSaving,
    presentationSummaryItems,
    saveRenderConfig,
    setRenderConfig,
  } = useReportPresentation({
    reportTypeId,
    columnDefinitions: TICKET_SUMMARY_COLUMN_DEFINITIONS,
  });
  const csvScopes = useMemo(
    () =>
      data.map((row) => ({
        ...row,
        scopeName: `${row.branchName} Branch`,
        completed: row.totalCompleted,
        noShow: row.totalNoShow,
        unattended: row.totalUnattended,
      })),
    [data]
  );

  return (
    <Box>
      {showTitle === 1 && (
        <Helmet>
          <title>
            {getReportRangeString(reportRange)} Organization Tickets Per Branch
          </title>
        </Helmet>
      )}
      {showFilterForm ? <FilterForm /> : <></>}
      {loading ? (
        <>
          <Loader />
        </>
      ) : (
        <>
          {" "}
          {!_.isEmpty(data) ? (
            <>
              <HStack flexDirection={"row"} justifyContent={"space-between"}>
                <Heading fontSize="34px" color="#222" fontWeight="700">
                  {title}

                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <Icon as={Info} w={5} h={5} ml={2} cursor="pointer" />
                    </Tooltip.Trigger>
                    <Tooltip.Positioner>
                      <Tooltip.Content>
                        {getReportDescription(reportRange).TicketsByBranch}
                      </Tooltip.Content>
                    </Tooltip.Positioner>
                  </Tooltip.Root>
                </Heading>
                <DownloadReport
                  reportRange={reportRange}
                  reportTypeId={reportTypeId}
                  renderConfig={effectiveRenderConfig}
                  onSave={saveRenderConfig}
                  isSaving={isSaving}
                  onDownloadCsv={() =>
                    downloadTicketSummaryCsvFiles({
                      scopes: csvScopes,
                      renderConfig: effectiveRenderConfig,
                      filenamePrefix: "organization_tickets_per_branch",
                    })
                  }
                />
              </HStack>
              <ReportPresentationControls
                renderConfig={effectiveRenderConfig}
                onChange={setRenderConfig}
                columnDefinitions={TICKET_SUMMARY_COLUMN_DEFINITIONS}
                defaultSortColumn="total"
              />
              <ReportPresentationSummary items={presentationSummaryItems} />
              <TicketsByBranchReport
                data={data}
                renderConfig={effectiveRenderConfig}
                onRenderConfigChange={setRenderConfig}
              />
            </>
          ) : (
        <SelectBranchInfo message={'Update Filters and Hit Search to retrieve data'}/>
      )}
        </>
      )}
    </Box>
  );
};

export default TicketsPerBranch;
